import { ConditionalValue } from '@chakra-ui/react'
import {
  Grip,
  Merge,
  Mic,
  MicOff,
  Pause,
  PhoneOff,
  PhoneOutgoing,
  Plus,
  Split,
  SquareParking,
  Volume1,
} from 'lucide-react'
import React, { forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  QuickActionButton,
  QuickActionButtonSizes,
} from '~/components/shared/QuickActionButton'
import { TranslationKeys } from '~/locales/i18next'

export enum ControlButtonTypes {
  AUDIO = 'AUDIO',
  HOLD = 'HOLD',
  KEYPAD = 'KEYPAD',
  MUTE = 'MUTE',
  PARK = 'PARK',
  TRANSFER = 'TRANSFER',
  NEW = 'NEW',
  MERGE = 'MERGE',
  SPLIT = 'SPLIT',
  END = 'END',
}
const ControlButtonIconConfig = {
  [ControlButtonTypes.AUDIO]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: Volume1,
    labelKey: 'callControlButton.audioSettings',
    styleOverride: {},
  },
  [ControlButtonTypes.HOLD]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: Pause,
    labelKey: 'callControlButton.hold',
    styleOverride: {},
  },
  [ControlButtonTypes.KEYPAD]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: Grip,
    labelKey: 'callControlButton.keypad',
    styleOverride: {},
  },
  [ControlButtonTypes.MUTE]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: MicOff,
    labelKey: 'callControlButton.mute',
    styleOverride: {},
  },
  [ControlButtonTypes.PARK]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: SquareParking,
    labelKey: 'callControlButton.park',
    styleOverride: {},
  },
  [ControlButtonTypes.TRANSFER]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: PhoneOutgoing,
    labelKey: 'callControlButton.transfer',
    styleOverride: {},
  },
  [ControlButtonTypes.NEW]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: Plus,
    labelKey: 'callControlButton.newCall',
    styleOverride: {},
  },
  [ControlButtonTypes.MERGE]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: Merge,
    labelKey: 'callControlButton.mergeCalls',
    styleOverride: {},
  },
  [ControlButtonTypes.SPLIT]: {
    variant: QuickActionButtonVariants.Default_200,
    icon: Split,
    labelKey: 'callControlButton.splitConference',
    styleOverride: {},
  },
  [ControlButtonTypes.END]: {
    variant: QuickActionButtonVariants.Destructive_300,
    icon: PhoneOff,
    labelKey: 'callControlButton.endCall',
    styleOverride: {},
  },
}

const TOGGLE_BUTTON_TYPES = new Set([
  ControlButtonTypes.MUTE,
  ControlButtonTypes.HOLD,
])

interface ControlButtonProps {
  type: ControlButtonTypes
  size: ConditionalValue<QuickActionButtonSizes>
  onClick: () => void
  isActive?: boolean
  disabled?: boolean
  /** For buttons that open a drawer: whether that drawer is open. */
  ariaExpanded?: boolean
  /** For buttons that open a drawer: the drawer element's id. */
  ariaControls?: string
}
export const CallControlButton = forwardRef(function CallControlButton(
  {
    type,
    size,
    onClick,
    isActive,
    disabled,
    ariaExpanded,
    ariaControls,
  }: ControlButtonProps,
  ref: React.Ref<HTMLButtonElement>,
) {
  const { t } = useTranslation()
  const handleClick = () => {
    if (disabled) return
    onClick()
  }

  const { variant, icon, labelKey } = ControlButtonIconConfig[type]
  const resolvedIcon =
    type === ControlButtonTypes.MUTE && !isActive ? Mic : icon
  const label = t(labelKey as TranslationKeys)

  return (
    <QuickActionButton
      ref={ref}
      isActive={isActive}
      disabled={disabled}
      variant={variant}
      size={size}
      actionIcon={resolvedIcon}
      tooltipText={label}
      onClick={handleClick}
      aria-pressed={TOGGLE_BUTTON_TYPES.has(type) ? !!isActive : undefined}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
    />
  )
})
